import React, { useMemo, useState } from 'react';
import { SectionHead } from './ui/SectionHead';
import { SectionReveal } from './ui/SectionReveal';
import { articles, authorPages } from '../data/articles';
import type { OutletSlug } from '../types/content';
type Filter = OutletSlug | 'all';
const outletOrder: OutletSlug[] = ['forbes', 'techcrunch', 'thenextweb', 'fortune', 'fastcompany', 'hackernoon', 'builtin', 'devops'];
export function ArticleIndex() {
  const [filter, setFilter] = useState<Filter>('all');
  const filters = useMemo(() => {
    const counts = outletOrder.map(slug => ({
      slug,
      label: articles.find(article => article.outlet === slug)?.outletLabel ?? slug,
      count: articles.filter(article => article.outlet === slug).length
    }));
    return [{
      slug: 'all' as const,
      label: 'All',
      count: articles.length
    }, ...counts];
  }, []);
  const visible = filter === 'all' ? articles : articles.filter(article => article.outlet === filter);
  return <section id="index" className="border-t border-rule py-[clamp(66px,9vw,114px)]" data-id="element-10" data-mp="1cft6yygscl5b">
      <div className="mx-auto max-w-shell px-[clamp(20px,5vw,64px)]" data-id="element-11" data-mp="1gglg7rpnqq1l">
        <SectionHead index="04" kicker="The index" title="Everything I have published, in one log." intro={`${articles.length} articles on engineering measurement, AI in the SDLC, developer experience, and building a company without institutional capital. Reverse chronological. Every entry links to the original.`} data-id="element-12" data-mp="1lgzpfd2zw617" />

        <div role="group" aria-label="Filter articles by publication" className="mt-11 flex flex-wrap gap-2" data-id="element-13" data-mp="12qrrrai2e7se">
          {filters.map(item => {
          const active = filter === item.slug;
          return <button key={item.slug} onClick={() => setFilter(item.slug)} aria-pressed={active} className={`border px-3.5 py-2.5 font-mono text-[11.5px] uppercase tracking-[0.1em] transition-colors duration-200 ease-editorial ${active ? 'border-ink bg-ink text-bone' : 'border-rule bg-transparent text-ink-70 hover:border-ink hover:text-ink'}`} data-id="element-14" data-mp="a1azccug2qw6">
                {item.label}
                <span className="pl-2 opacity-60" data-id="element-15" data-mp="btk9m1sb0jrt">{item.count}</span>
              </button>;
        })}
        </div>

        <div className="mt-[34px] border-t border-ink" data-id="element-16" data-mp="3g5xu9mie3yx8">
          <div className="hidden grid-cols-[104px_152px_1fr] gap-5 border-b border-rule py-[11px] md:grid" data-id="element-17" data-mp="3g7ipzfgfxnwo">
            <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-45" data-id="element-18" data-mp="3u2ujjjiv18c0">
              Date
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-45" data-id="element-19" data-mp="19egky5ssgmrb">
              Publication
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-45" data-id="element-20" data-mp="14fdfik8wceit">
              Title
            </span>
          </div>

          <ul data-id="element-21" data-mp="2rccxdf9uqjmw">
            {visible.map(article => <li key={article.title} data-id="element-22" data-mp="2re62gq4tgvss">
                <a href={article.url} target="_blank" rel="noopener noreferrer" className="group relative grid grid-cols-1 gap-1.5 border-b border-rule-soft py-4 no-underline transition-[padding-left] duration-200 ease-editorial hover:pl-3.5 md:grid-cols-[104px_152px_1fr] md:items-baseline md:gap-5" data-id="element-23" data-mp="2dd3h6flnbpix">
                  <span aria-hidden="true" className="absolute bottom-0 left-0 top-0 w-0.5 origin-top scale-y-0 bg-signal transition-transform duration-200 ease-editorial group-hover:scale-y-100" data-id="element-24" data-mp="2wbpk800q3qph" />
                  <span className="font-serif text-[17.5px] leading-[1.3] tracking-[-0.012em] underline-offset-4 group-hover:underline md:col-start-3 md:row-start-1 md:text-[19px]" data-id="element-25" data-mp="3oyseuh2a3hag">
                    {article.title}
                  </span>
                  <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1 md:contents" data-id="element-26" data-mp="2065yqhi50yuq">
                    <span className="font-mono text-[11.5px] tracking-[0.06em] text-ink-45 md:col-start-1 md:row-start-1" data-id="element-27" data-mp="1w4kol3wzmyo8">
                      {article.date}
                    </span>
                    <span className="font-mono text-[11.5px] uppercase tracking-[0.11em] text-ink-45 transition-colors duration-200 ease-editorial group-hover:text-signal md:col-start-2 md:row-start-1" data-id="element-28" data-mp="1r7kfsl8r7htf">
                      {article.outletLabel}
                    </span>
                  </span>
                </a>
              </li>)}
          </ul>
        </div>

        <div className="mt-[34px] flex flex-wrap items-center gap-[18px] border-t border-rule pt-6" data-id="element-29" data-mp="2a3u37z5jpkq3">
          <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-ink-45" data-id="element-30" data-mp="37l2aia2yliwm">
            Author pages
          </span>
          {authorPages.map(page => <a key={page.label} href={page.url} target="_blank" rel="noopener noreferrer" className="border-b border-transparent pb-[3px] font-mono text-[11.5px] uppercase tracking-[0.1em] text-ink-45 no-underline transition-colors duration-200 ease-editorial hover:border-signal hover:text-signal" data-id="element-31" data-mp="3a57b3dsgdlvv">
              {page.label}
            </a>)}
        </div>
      </div>
    </section>;
}